import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { API_BASE_URL } from 'Src/stores/appEnv';

import styles from './SettingsLatencyTest.module.scss';

const sectionId = 'LatencyTest';

const apiFetch = async () => {
  try {
    const start = Date.now();
    const response = await fetch(`${API_BASE_URL}/api/healthz`);
    if (!response.ok) throw new Error(`Response status: ${response.status}`);
    return Date.now() - start;
  } catch (error) {
    return error;
  }
};

const SettingsLatencyTest: FC = () => {
  const { t } = useTranslation();

  const [deltas, setDeltas] = useState<number[]>([]);
  const [firstDelta, setFirstDelta] = useState<number>(0);
  const [errors, setErrors] = useState<unknown[]>([]);

  const deltaAverage = useMemo(() => {
    if (deltas.length === 0) return 0;
    return deltas.reduce((sum, currentValue) => sum + currentValue, 0) / deltas.length;
  }, [deltas]);

  const onLatencyTest = async () => {
    setDeltas([]);
    setErrors([]);
    setFirstDelta(0);
    const deltaArr = [];
    const errArr = [];
    for (let i = 0; i < 11; i++) {
      const resp = await apiFetch();
      if (typeof resp === 'number') {
        // The first time you load, the first fetch incurs the tcp setup of the http connection, and can be an outlier.
        // We still show the first one as setup, but take the average of the 10 requests after because the browser will 
        // leave that http connection open, and then it is a true rtt without initial setup time.
        if (i === 0) setFirstDelta(resp);
        else deltaArr.push(resp);
      } else {
        errArr.push(resp);
      }
    }
    setDeltas(deltaArr);
    setErrors(errArr);
  };

  return (
    <Flex data-testid={sectionId} className={styles.root}>
      <CTA color="primary" label={t('settings.labels.startNetworkTest')} onClick={onLatencyTest} />
      <Copy value={t('settings.labels.deltas', { deltas: `${firstDelta},${deltas.toString()}` })} />
      <Copy value={t('settings.labels.average', { deltaAverage })} />
      <Copy value={t('settings.labels.errors', { errors: errors.length })} />
    </Flex>
  );
};

export { SettingsLatencyTest };
